import { ComposerTranslation } from 'vue-i18n'
import { topLevelClass } from '@/constants'
import { twColor } from '@/utils/colors'
import { useCountries } from '@/composables/useCountries'
import type { EChartsOption } from 'echarts'
import type { ThemeOption } from 'echarts/types/src/util/types'

/**
 * Set the color of a chart element.
 */
function setColor(params: any) {
    const data = params.data as { iso: string; color?: string }
    if (!data) return twColor('deep-blue')

    if (data.iso) {
        const isoColor = useCountries().getColor(data.iso)
        if (isoColor) return isoColor
    }

    return data.color || twColor('deep-blue')
}

/**
 * Set the name of a chart element.
 */
function setName(params: any): string {
    const data = params.data as { iso: string }

    if (!data) return params.name

    if (data.iso) {
        const countryName = useCountries().getName(data.iso)
        if (countryName) {
            return countryName
        }
    }

    return params.name
}

/**
 * Create the tooltip element.
 */
export function tooltip(
    _name: string,
    country: string,
    value: number | string | (number | string)[],
    format = defaultFormatter()
) {
    const countryName = useCountries().getName(country) || country
    value = Array.isArray(value) ? value[0] : value

    if (typeof value === 'number') {
        value = format(value)
    }

    return `<div class="flex flex-col gap-2 max-w-sm whitespace-normal max-sm:max-w-[75vw]">
        <div class="flex flex-col gap-2">
        <p class="text-white font-bold mr-4">${countryName}</p>
        <div class="border border-white px-2 py-1 flex items-center justify-center text-center">
            <p class="font-bold text-base text-white">${value}</p>
        </div>
        </div>
        </div>`
}

/**
 * Create the tooltip element.
 */
export function tooltipWithTitle(
    text: string,
    country: string,
    value: number | string | (number | string)[],
    format = defaultFormatter()
) {
    const countryName = useCountries().getName(country) || country
    value = Array.isArray(value) ? value[0] : value

    if (typeof value === 'number') {
        value = format(value)
    }

    return `<div class="flex flex-col gap-2 max-w-sm whitespace-normal max-sm:max-w-[75vw]">
        <div class="flex items-center justify-between gap-2">
        <p class="text-white font-bold mr-4">${countryName}</p>
        <div class="border border-white px-2 py-1 flex items-center justify-center text-center">
            <p class="font-bold text-xs text-white">${value}</p>
        </div>
        </div>
        <p class="text-white text-sm">${text}</p>
        </div>`
}

/**
 * Full Echarts theme.
 */
export default (t: ComposerTranslation): EChartsOption & ThemeOption => ({
    textStyle: {
        fontFamily: 'Source Sans Prop, sans-serif',
        fontSize: 12,
        fontWeight: 'normal'
    },
    title: {
        textStyle: {
            fontFamily: 'Rubik, sans-serif',
            fontWeight: 'bold',
            color: twColor('deep-blue'),
            overflow: 'break',
            fontSize: 20
        }
    },
    tooltip: {
        backgroundColor: twColor('deep-blue'),
        borderWidth: 0,
        padding: 10,
        renderMode: 'auto',
        className: topLevelClass,
        confine: true,
        alwaysShowContent: false,
        hideDelay: 100,
        formatter: (params: any) => {
            const name = params.name as string
            const country = params.seriesName as string
            const value = params.data.value as number
            return tooltip(name, country, value)
        },
        textStyle: {
            color: 'white'
        },
        appendToBody: true
    },
    visualMap: {
        left: 'center',
        itemSymbol: 'circle',
        bottom: '2.5%',
        orient: 'horizontal',
        itemWidth: 15,
        itemHeight: 15,
        itemGap: 10,
        textGap: 5,
        textStyle: {
            color: 'black',
            fontSize: 10
        }
    },
    dataZoom: {
        backgroundColor: 'white',
        borderRadius: 10,
        fillerColor: 'gray',
        borderColor: 'gray',
        showDetail: false,
        showDataShadow: false,
        brushSelect: false,
        zoomLock: true,
        // svg path of circle in dataZoom slider
        handleIcon: 'M 0 0 m -8 0 a 8 8 0 1 0 16 0 a 8 8 0 1 0 -16 0',
        handleStyle: {
            color: 'gray',
            borderWidth: 0
        }
    },
    bar: {
        itemStyle: {
            color: setColor
        },
        label: {
            formatter: setName,
            color: 'black',
            fontSize: 12,
            overflow: 'break'
        }
    },
    pictorialBar: {
        itemStyle: {
            color: '#F2F2F2'
        },
        barGap: '-100%',
        symbolBoundingData: 1
    },
    grid: {
        containLabel: false,
        top: '5%',
        left: '7.5%',
        bottom: '5%',
        right: '7.5%'
    },
    graph: {
        label: {
            color: twColor('deep-blue'),
            show: true,
            position: 'top'
        },
        itemStyle: {
            color: setColor
        }
    },
    pie: {
        blur: {
            itemStyle: {
                opacity: 0.2
            }
        },
        universalTransition: true
    },
    map: {
        roam: true,
        map: 'world',
        label: {
            show: false
        },
        itemStyle: {
            areaColor: twColor('neutral')
        },
        emphasis: {
            label: {
                show: false
            },
            itemStyle: {
                areaColor: undefined
            }
        }
    },
    scatter: {
        itemStyle: {
            color: setColor
        },
        symbolSize: 20,
        blur: {
            itemStyle: {
                opacity: 0.2
            }
        },
        label: {
            show: true,
            position: 'inside',
            formatter: '{b}',
            color: 'black',
            overflow: 'break'
        }
    },
    categoryAxis: {
        nameLocation: 'middle',
        nameGap: 10,
        nameTextStyle: {
            fontFamily: 'Rubik',
            fontSize: 14,
            fontWeight: 'bold',
            color: 'black'
        },
        axisLabel: {
            show: false,
            interval: 0,
            color: 'black',
            fontSize: 12,
            overflow: 'break',
            fontFamily: 'Source Sans Prop, sans-serif'
        },
        nameTruncate: {
            maxWidth: 200,
            ellipsis: '...',
            placeholder: '...'
        }
    },
    valueAxis: {
        nameRotate: 90,
        nameGap: 50,
        nameLocation: 'middle',
        nameTextStyle: {
            fontFamily: 'Rubik',
            fontSize: 14,
            fontWeight: 'bold',
            color: 'black'
        },
        nameTruncate: {
            maxWidth: 200,
            ellipsis: '...',
            placeholder: '...'
        },
        splitLine: {
            show: false
        },
        axisLine: {
            show: false
        },
        axisTick: {
            show: false
        },
        axisLabel: {
            show: true,
            interval: 0,
            color: 'black',
            fontSize: 12,
            overflow: 'break',
            fontFamily: 'Source Sans Prop, sans-serif'
        }
    },
    logAxis: {
        nameRotate: 90,
        nameGap: 50,
        nameLocation: 'middle',
        nameTextStyle: {
            fontFamily: 'Rubik',
            fontSize: 14,
            fontWeight: 'bold',
            color: 'black'
        },
        nameTruncate: {
            maxWidth: 200,
            ellipsis: '...',
            placeholder: '...'
        },
        splitLine: {
            show: false
        },
        axisLine: {
            show: false
        },
        axisTick: {
            show: false
        },
        axisLabel: {
            color: 'black',
            fontWeight: 'normal'
        }
    },
    legend: {
        icon: 'circle',
        textStyle: {
            color: 'black',
            fontFamily: 'Source Sans Pro',
            fontSize: 12
        },
        tooltip: {
            show: true,
            formatter(a) {
                return t('click-to-toggle-display', { name: a.name })
            }
        }
    }
})
